import {
  NonNullableOrganisationState,
  NonNullableRecognitionApplicationState,
} from "../../../redux/state";
import { ApplicationAppBar } from "../ApplicationAppBar";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import {
  createApplicationScopes,
  deleteSelectedScopes,
  toggleDeleteScope,
  addScope,
  updateScope,
  loadingApplicationScopes,
} from "../../../redux/actions";
import { AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { ScopesList } from "@ofqual/portal-organisations-scope/dist/module/presentation/scopes/ScopesList";
import { applicationScopesSelector, authSelector } from "../../../redux/selectors";
import { Skeleton } from "@material-ui/lab";
import { Scope } from "@ofqual/portal-organisations-scope/dist/module/domain/Scope";
import { LookupTrackerStore } from "@ofqual/portal-core/core/types";
import { Prompt } from "react-router-dom";
import { RecognitionStatus } from "../../../domain/RecognitionStatus";

type ScopesProps = {
  orgState: NonNullableOrganisationState;
  applicationState: NonNullableRecognitionApplicationState;
  canEdit: boolean;
};

export const Scopes: React.FC<ScopesProps> = ({
  orgState,
  applicationState,
  canEdit,
}) => {
  const { org } = orgState;
  const dispatch = useDispatch();
  const { scopes, status, lastCrudResult } = useSelector(
    applicationScopesSelector
  );

  const orgId = org.id;
  const applicationId = applicationState.application.id;

  const isDirtyScopes = scopes.some((x: any) => !x.orgId || !x.applicationId);

  useEffect(() => {
    dispatch(loadingApplicationScopes(orgId, applicationId));
  }, [dispatch, orgId, applicationId]);

  const handleSubmit = () =>
    dispatch(createApplicationScopes(org.id, applicationId, scopes));

  const onToggleDeleteScopes = (scopes: Scope[]) =>
    dispatch(toggleDeleteScope(scopes));

  const onDeleteScopes = () => dispatch(deleteSelectedScopes());

  const onAddScope = (scope: Scope) => {
    dispatch(addScope(scope));
  };

  const onUpdateScope = (
    scope: Scope,
    lookupTrackingInfo: LookupTrackerStore
  ) => {
    dispatch(updateScope(scope, lookupTrackingInfo));
  };

  const { experimentalFeatures } = useAuth(authSelector);

  return (
    <>
      <Prompt
        message="There are unsaved changes in the form.  Are you sure you want to navigate away from this page and lose these changes?"
        when={isDirtyScopes}
      />
      <ApplicationAppBar
        orgState={orgState}
        applicationState={applicationState}
        tabName="Scopes"
        canEdit={canEdit}
      >
        {applicationState?.application?.status === RecognitionStatus.Pending &&
          canEdit && (
            <AppBarSaveButton
              onClick={handleSubmit}
              isSubmitting={status === "UPDATING"}
              isSubmitted={lastCrudResult.isSuccess}
              dataTest="save-button"
            />
          )}
      </ApplicationAppBar>

      {status === "LOADED" ? (
        <>
          <ExperimentalFeature
            experimentalModuleName="recognition"
            experimentalFeatureVisibleName="allow-set-scope-start"
            experimentalFeatures={experimentalFeatures}
          >
            <ScopesList
              scopes={scopes}
              canEdit={canEdit}
              actions={{
                onAddScope,
                onDeleteScopes,
                onToggleDeleteScopes,
                onUpdateScope,
              }}
              allowSetScopeStart={true}
            />
          </ExperimentalFeature>
          <ExperimentalFeature
            experimentalModuleName="recognition"
            experimentalFeatureHiddenName="allow-set-scope-start"
            experimentalFeatures={experimentalFeatures}
          >
            <ScopesList
              scopes={scopes}
              canEdit={canEdit}
              actions={{
                onAddScope,
                onDeleteScopes,
                onToggleDeleteScopes,
                onUpdateScope,
              }}
            />
          </ExperimentalFeature>
        </>
      ) : (
        <>
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="rect" height={118} />
        </>
      )}
    </>
  );
};
